/**
 * QUEM DESLOGA, QUEM TENTA DE NOVO, E QUEM PODE FALAR DO RELÓGIO.
 *
 * INCIDENTE 28/07 00:42: o dono foi expulso do painel com "Sua sessão expirou.
 * Confira a DATA E HORA do computador". O token dele valia até 01:22. O que
 * quebrou foi a validação no servidor (uma chamada de rede que deu soluço), e
 * o front traduziu o 401 para a única história que sabia contar: "a culpa é sua
 * e do seu relógio". Ver `src/api/middleware/auth.ts` (vereditoToken).
 *
 * Agora o servidor separa os dois casos — 401 = o token não presta mesmo;
 * 5xx = não deu pra validar, e é nosso. Este módulo é o lado de cá dessa
 * conversa, em funções PURAS pra poder ser testado por sabotagem:
 *
 *   deveDeslogar      → só o 401 tira alguém do sistema.
 *   deveTentarDeNovo  → 5xx/rede se resolvem sozinhos; 4xx não melhoram tentando.
 *   TEXTO_ERRO_EMPRESA→ a frase do relógio mora AQUI, e só no caminho "sessao".
 */

/**
 * Por que a pessoa não conseguiu carregar a empresa dela.
 *
 *  - "sessao"      → 401: a sessão morreu de verdade (ou o relógio da máquina
 *                    está errado a ponto do token nascer inválido).
 *  - "sem-empresa" → nenhuma empresa ativa vinculada. É dado, não sessão.
 *  - "desligado"   → a conta foi desativada (403 user_desligado).
 *  - "falha"       → rede/500/503: nem deu pra perguntar. Inclui os 503 que o
 *                    backend assume como dele (`vinculo_indisponivel`,
 *                    `token_indisponivel`, `perfil_indisponivel`).
 */
export type EmpresaErro = "sessao" | "sem-empresa" | "desligado" | "falha";

/**
 * O código do backend manda mais que o status: 401 e 403 já significaram coisas
 * diferentes aqui, e tratar todo 403 como "sessão expirou" mandaria a pessoa
 * relogar por um problema que relogar não resolve.
 */
export function classificarErroEmpresa(status: number, codigo?: string): EmpresaErro {
  if (codigo === "empresa_sem_vinculo") return "sem-empresa";
  if (codigo === "user_desligado") return "desligado";
  if (status === 401) return "sessao";
  return "falha";
}

/**
 * SÓ O 401 DESLOGA.
 *
 * Um 503 significa "não conseguimos confirmar" — deslogar aí é transformar um
 * soluço de infraestrutura em perda de trabalho. Qualquer outro status também
 * não: 403 é falta de permissão (relogar não resolve), 5xx é nosso, 0 é rede.
 */
export function deveDeslogar(status: number): boolean {
  return status === 401;
}

/**
 * O que melhora sozinho se esperar um instante: rede caída (status 0), 5xx,
 * "tempo esgotado" (408) e limite de taxa (429). O resto é resposta definitiva —
 * insistir só faz o usuário esperar mais pra ver o mesmo erro.
 */
export function deveTentarDeNovo(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

/**
 * Espera curta e crescente entre tentativas (0-based): 400ms, 1200ms.
 * Curta de propósito — quem está olhando a tela não pode sentir a espera.
 */
export function esperaTentativa(tentativa: number): number {
  return 400 * Math.pow(3, tentativa);
}

/**
 * A CÓPIA DAS TELAS DE ERRO — fonte única.
 *
 * 🔒 A frase sobre DATA E HORA fica presa ao caso "sessao" e a nenhum outro.
 *    Era ela que aparecia no 401 de infraestrutura e mandava o dono conferir o
 *    relógio de um computador que estava certo. `tests/erro-sessao-nao-culpa-o-
 *    usuario.test.ts` quebra se a frase vazar pros outros casos.
 */
export const TEXTO_ERRO_EMPRESA: Record<EmpresaErro, { titulo: string; corpo: string }> = {
  sessao: {
    titulo: "Sua sessão expirou",
    corpo:
      "Entre de novo. Se isso se repetir, confira a DATA E HORA do computador — relógio atrasado ou adiantado faz o servidor recusar o acesso.",
  },
  "sem-empresa": {
    titulo: "Sua conta não está vinculada a nenhuma empresa",
    corpo:
      "O acesso existe, mas falta o vínculo com a empresa. Peça a um administrador pra liberar — não é algo que dê pra resolver por aqui.",
  },
  desligado: {
    titulo: "Seu acesso foi encerrado",
    corpo:
      "Esta conta foi desativada. Se isso não parece certo, fale com um administrador da empresa.",
  },
  falha: {
    titulo: "Instabilidade nossa",
    corpo:
      "Não consegui carregar seus dados agora — o problema é nosso ou da conexão, não é a sua conta. Tente de novo em instantes; se insistir, avise o suporte.",
  },
};

/**
 * 🔴 O BOOT PRECISA DE PRAZO — senão ele não falha, ele PARA.
 *
 * INCIDENTE 28/07: `VORTEK / Carregando…` pra sempre, em 3 rotas, console
 * limpo, nenhum erro. A rede mostrava `GET /api/me/perfil` PENDENTE e nunca
 * respondendo. Não era lentidão: era uma promessa que não termina.
 *
 * `deveTentarDeNovo` acima só serve pra erro que ACONTECEU. Uma requisição que
 * fica pendurada nunca lança, então nunca é repetida, nunca cai no `catch` e
 * nunca chega no `finally` que destrava a tela — o painel fica esperando uma
 * resposta que não vem, sem ter como saber que está esperando.
 *
 * `comPrazo` transforma "nunca" em "falhou": passado o prazo, a promessa REJEITA
 * com status 408 — que `deveTentarDeNovo` já classifica como "passa sozinho".
 * Assim o caminho de erro que já existe (mensagem honesta, botão de tentar de
 * novo, perfil mínimo) volta a ser alcançável.
 *
 * Puro e sabotável: quem chama injeta o relógio.
 */
export class PrazoEsgotado extends Error {
  /** 408 de propósito: `deveTentarDeNovo(408)` já é true. */
  readonly status = 408;
  constructor(public readonly ms: number) {
    super(`prazo de ${ms}ms esgotado`);
    this.name = "PrazoEsgotado";
  }
}

export function comPrazo<T>(
  promessa: Promise<T>,
  ms: number,
  agendar: (fn: () => void, ms: number) => unknown = setTimeout,
  cancelar: (id: never) => void = clearTimeout as unknown as (id: never) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = agendar(() => reject(new PrazoEsgotado(ms)), ms);
    promessa.then(
      (v) => {
        cancelar(id as never);
        resolve(v);
      },
      (e) => {
        cancelar(id as never);
        reject(e);
      },
    );
  });
}

/**
 * Repete `fn` enquanto o erro for do tipo que passa sozinho. `lerStatus` isola
 * este módulo do ApiError (que vive em `lib/api`) — assim ele continua puro e
 * testável sem arrastar o cliente de API junto.
 */
export async function comTentativas<T>(
  fn: () => Promise<T>,
  lerStatus: (erro: unknown) => number,
  opts?: { tentativas?: number; dormir?: (ms: number) => Promise<void> }
): Promise<T> {
  const tentativas = opts?.tentativas ?? 2; // 1 original + 2 repetições
  const dormir = opts?.dormir ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  let ultimo: unknown;
  for (let i = 0; i <= tentativas; i++) {
    try {
      return await fn();
    } catch (e) {
      ultimo = e;
      if (i === tentativas || !deveTentarDeNovo(lerStatus(e))) throw e;
      await dormir(esperaTentativa(i));
    }
  }
  throw ultimo;
}
